import { Flex } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useModalState } from '~/atoms/ModalState'
import { useSipInstance } from '~/atoms/SipInstanceState'
import { StorageKeys } from '~/atoms/StorageKeys'
import { useUserState } from '~/atoms/UserState'
import { PhoneStatus } from '~/atoms/WebphoneState'
import {
  ResponsivePage,
  useTogglePanel,
} from '~/components/layout/ResponsivePage'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useDebounce } from '~/hooks/useDebounce'
import { useNetworkConnectivity } from '~/hooks/useNetworkConnectivity'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { AnalyticsNamespace } from '~/types/Analytics'
import { WebPhoneAnalyticsEvents } from '~/types/Analytics/WebPhoneAnalyticsEvents'
import { SettingsType } from '~/types/Settings'
import { SearchValue } from '~/types/Shared'
import { hasValue } from '~/utils/common'
import { formatPhoneNumber, getValidNumber } from '~/utils/formatter'
import { getDisplayCallerInfo } from '~/utils/phoneHelpers'

import { toDialTarget } from '@mangovoice/core'
import { ResourceLoadingState } from '@mangovoice/types'

import {
  ContactDetails,
  ContactNumber,
  useContactsViewModel,
} from '../Contacts/viewModels/Contacts.viewmodel'
import { useRecentCalls } from './atoms/RecentCalls.atom'
import { CallErrorView } from './CallErrorView'
import { ExtensionSelector } from './ExtensionSelector'
import { InCallView } from './InCallView'
import { OutOfCallView } from './OutOfCallView'
import { PhoneInitializingView } from './PhoneInitializingView'
import { QuickDialView } from './QuickDialView'
import { useWebPhoneRenderLoopDiagnostics } from './useWebPhoneRenderLoopDiagnostics'

const tabStateAtom = atomWithStorage<'dialpad' | 'search'>(
  StorageKeys.WebphoneTabState,
  'dialpad',
)

const DEBOUNCE_WAIT_MS = 250

const WebPhoneView = () => {
  const [tabState, setTabState] = useAtom(tabStateAtom)
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [debounceSearchValue, setDebounceSearchValue] = useState(
    SearchValue.empty,
  )
  const cancelSearchRef = useRef<(() => void) | undefined>(undefined)

  const [dtmfHistory, setDtmfHistory] = useState<string>('')
  const [autoDialNumber, setAutoDialNumber] = useState<string | undefined>(
    undefined,
  )
  // Store the phone number when an inbound call comes in, so we can restore it after the call ends
  const savedPhoneNumberRef = useRef<string>('')
  const savedSearchValueRef = useRef<SearchValue>(SearchValue.empty)
  const [isDialInputFocusRequested, setIsDialInputFocusRequested] =
    useState(false)
  const handleDialInputFocusRequestHandled = useCallback(() => {
    setIsDialInputFocusRequested(false)
  }, [])
  const { pbxId } = useUserState()
  const {
    callModal: [, setCallModalState],
    isAnyModalOpen,
  } = useModalState()

  useTogglePanel()

  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const {
    sipInstance: [getSipInstance],
  } = useSipInstance()

  const {
    state: {
      phoneCalls: {
        active: { activeCall },
        conference: {
          conferencedCalls,
          startTime: conferenceStartTime,
          isMuted: conferenceIsMuted,
          isOnHold: conferenceIsOnHold,
        },
        list: { callList },
        invite: { pendingInvitation },
        isAtMaxCallLimit,
      },
      phoneStatus,
      registrationError,
      extensions,
      trackedExtensions,
      trackedParkingLots,
      audioDevices: {
        output: { devices: outputs, preference: selectedOutputDeviceId },
        input: { devices: inputs, preference: selectedInputDeviceId },
      },
      speedDial,
    },
    actions: {
      resetConnection,
      startCall,
      muteCall,
      holdCall,
      transferCall,
      parkCall,
      unparkCall,
      endCall,
      sendDtmf,
      switchAudioInput,
      switchAudioOutput,
      mergeCalls,
      splitCalls,
      endConferenceCall,
      toggleHoldConference,
      toggleMuteConference,
    },
  } = usePhoneEndpoint({
    onCallEnd: () => {
      setDtmfHistory('')
    },
    onFinalCallEnd: () => {
      // Restore the saved phone number if we had one before the call
      if (savedPhoneNumberRef.current) {
        setPhoneNumber(savedPhoneNumberRef.current)
        savedPhoneNumberRef.current = ''
      } else {
        setPhoneNumber('')
      }
      // Restore the saved search value if we had one
      if (savedSearchValueRef.current.value) {
        setDebounceSearchValue(savedSearchValueRef.current)
        savedSearchValueRef.current = SearchValue.empty
      } else {
        setDebounceSearchValue(SearchValue.empty)
      }
      setDtmfHistory('')
      setIsDialInputFocusRequested(true)
    },
  })

  // Save the phone number when an inbound call comes in, so we can restore it after the call ends
  useEffect(() => {
    if (
      pendingInvitation &&
      phoneNumber &&
      !activeCall &&
      !savedPhoneNumberRef.current
    ) {
      savedPhoneNumberRef.current = phoneNumber
      savedSearchValueRef.current = debounceSearchValue
    }
  }, [pendingInvitation, phoneNumber, debounceSearchValue, activeCall])

  const { recentContacts } = useRecentCalls(pbxId, extensions)

  const {
    state: {
      searchValue: contactsSearchValue,
      contactsListState: { data: contactsList },
      contactsListLoadingState,
      contactSources,
      contactSourcesLoadingState,
      selectedContactSourceId,
    },
    actions: {
      setSearchValue,
      setSelectedContactSource,
      getNextContactsPage,
      getContactDetails,
      getContactSources,
    },
  } = useContactsViewModel()

  useEffect(() => {
    if (
      !contactSources &&
      contactSourcesLoadingState?.state !== ResourceLoadingState.Loading
    ) {
      getContactSources()
    }
    if (selectedContactSourceId) {
      setSelectedContactSource(undefined, false)
    }
  }, [
    contactSources,
    contactSourcesLoadingState?.state,
    selectedContactSourceId,
    getContactSources,
    setSelectedContactSource,
  ])

  const isSearchLoading =
    !contactsListLoadingState?.state ||
    contactsListLoadingState.state === ResourceLoadingState.Loading
  const searchResults = contactsList.map(getContactDetails)

  const location = useLocation()
  const navigate = useNavigate()
  const [queryParams] = useSearchParams()
  const number = queryParams.get('number')
  const autoDial = queryParams.get('autoDial')
  const isOnline = useNetworkConnectivity({
    playConnectionChangeSound: false,
    reportAnalytics: false,
  })

  //only recompute route number if the route hash or number changes
  useEffect(() => {
    if (
      number &&
      (phoneNumber === '' || phoneNumber !== number) &&
      !autoDialNumber
    ) {
      setTabState('dialpad')
      setPhoneNumber(number)

      if (autoDial) {
        setAutoDialNumber(number)
      }

      navigate(ApplicationRoutes.WEB_PHONE, {
        replace: true,
      })
    }
  }, [location.hash, number])

  //useDebounce fires on mount too; the initial fetch happens in the mount
  //effect below, so only requery once the search has actually changed
  const hasSearchChanged = useRef(false)

  useDebounce(
    () => {
      if (!hasSearchChanged.current) return
      cancelSearchRef.current = setSearchValue(debounceSearchValue, true)
    },
    DEBOUNCE_WAIT_MS,
    [debounceSearchValue],
  )

  //on mount, clear any search left in the shared view model and load the
  //contacts list right away (it previously only loaded via the debounce
  //firing after mount)
  useEffect(() => {
    cancelSearchRef.current = setSearchValue(SearchValue.empty, true)
  }, [])

  function handleSearchTermChanged(searchValue: SearchValue) {
    hasSearchChanged.current = true
    if (cancelSearchRef.current) {
      cancelSearchRef.current()
      cancelSearchRef.current = undefined
    }
    setDebounceSearchValue(searchValue)
  }

  const handleScrollEndReached = () => {
    getNextContactsPage()
  }

  const handlePhoneNumberChanged = (newNumber: string) => {
    const cleanedNumber = getValidNumber(newNumber)
    setPhoneNumber(cleanedNumber)
  }

  const handleDialButtonClick = (digit: string) => {
    setPhoneNumber((prev) => prev + digit)
  }

  const handleDeleteButtonClick = () => {
    setPhoneNumber((prev) => getValidNumber(prev).slice(0, -1))
  }

  const handleDtmfClick = (digit: string) => {
    if (!activeCall) return
    sendDtmf(activeCall.callUuid, digit)
    setDtmfHistory(() => dtmfHistory + digit)
  }

  const handlePhoneSettingsLinkClick = () => {
    navigate(`${ApplicationRoutes.SETTINGS}/${SettingsType.PHONE}`)
  }

  const handleNewCallWithTargetClick = (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
  ) => {
    setCallModalState({ open: true, callee })
  }
  const handleNewCallClick = () => {
    setCallModalState({ open: true })
  }

  const handleStartCall = (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
    outboundCallerIdName?: string,
  ) => {
    if (typeof callee === 'string') {
      const cleanedNumber = toDialTarget(callee)
      const isParkingLot = trackedParkingLots.some(
        (lot) => lot.targetNumber === cleanedNumber,
      )

      if (isParkingLot) {
        unparkCall(cleanedNumber)
      } else {
        startCall(cleanedNumber, outboundCallerIdName)
      }
    } else {
      startCall(callee, outboundCallerIdName)
    }
    track(WebPhoneAnalyticsEvents.StartCallFromPage)
  }

  const handleMergeCalls = () => {
    const callUuids = Array.from(callList.keys())
    mergeCalls(callUuids)
  }
  const handleSplitCalls = () => {
    splitCalls()
  }

  const handleEndCall = () => {
    if (conferencedCalls) {
      endConferenceCall()
      return
    }
    endCall(activeCall?.callUuid ?? '')
  }

  const handleTransferCall = (transferTarget: string) => {
    if (!activeCall) return
    void transferCall(activeCall.callUuid, transferTarget).catch(
      () => undefined,
    )
  }

  const handleParkCall = (parkTarget: string) => {
    if (!activeCall) return
    void parkCall(activeCall.callUuid, parkTarget).catch(() => undefined)
  }

  const handleMuteClick = () => {
    if (conferencedCalls) {
      toggleMuteConference()
      return
    }
    if (!activeCall) return
    muteCall(activeCall.callUuid)
  }

  const handleHoldClick = async () => {
    if (conferencedCalls) {
      await toggleHoldConference()
      return
    }
    if (!activeCall) return
    await holdCall(activeCall.callUuid)
  }

  const handleInputDeviceChange = (deviceId: string) => {
    switchAudioInput(deviceId)
  }

  const handleOutputDeviceChange = (deviceId: string) => {
    switchAudioOutput(deviceId)
  }

  const handleRetry = () => {
    resetConnection()
  }

  // Dev tool: Expose disconnect function for testing reconnect flow
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Extend Window interface for dev tools
      ;(window as any).__webPhoneDevTools = {
        simulateDisconnect: () => {
          // Get the SIP instance and dispose it to simulate disconnection
          const sipClient = getSipInstance?.()
          if (sipClient) {
            console.log('[WebPhone Dev Tool] Simulating disconnect...')
            sipClient.dispose().catch((error) => {
              console.error(
                '[WebPhone Dev Tool] Error disposing SIP client:',
                error,
              )
            })
          } else {
            console.warn('[WebPhone Dev Tool] No SIP client instance found')
          }
        },
        reconnect: () => {
          console.log('[WebPhone Dev Tool] Triggering reconnect...')
          resetConnection()
        },
        getStatus: () => {
          return {
            phoneStatus,
            isConnected:
              phoneStatus !== PhoneStatus.TRANSPORT_DISCONNECTED &&
              phoneStatus !== PhoneStatus.ERROR,
          }
        },
      }
      console.log(
        '[WebPhone Dev Tool] Available! Use window.__webPhoneDevTools.simulateDisconnect() to test reconnect flow',
      )
    }
    return () => {
      if (process.env.NODE_ENV === 'development') {
        delete (window as any).__webPhoneDevTools
      }
    }
  }, [phoneStatus, resetConnection, getSipInstance])

  const isConnected =
    isOnline &&
    phoneStatus !== PhoneStatus.TRANSPORT_DISCONNECTED &&
    phoneStatus !== PhoneStatus.ERROR

  if (autoDialNumber && phoneStatus === PhoneStatus.READY && !activeCall) {
    setAutoDialNumber(undefined)
    startCall(autoDialNumber)
  }

  const callerDisplayNames = useMemo(() => {
    if (conferencedCalls) {
      return conferencedCalls.map((call) => {
        const displayInfo = getDisplayCallerInfo(call, true) // true = prefer SIP
        if (displayInfo.name && displayInfo.name !== '') {
          return displayInfo.name
        } else {
          return formatPhoneNumber(displayInfo.number)
        }
      })
    }
    if (!activeCall) return ['Unknown']
    const displayInfo = getDisplayCallerInfo(activeCall, true) // true = prefer SIP
    if (displayInfo.name && displayInfo.name !== '') {
      return [displayInfo.name]
    } else {
      return [formatPhoneNumber(displayInfo.number)]
    }
  }, [activeCall, conferencedCalls])

  // Diagnostic only (Sentry WEB-APP-PRODUCTION-6ZB): capture a PHI-safe state
  // snapshot on the next React #185 render loop so the trigger can be pinpointed.
  useWebPhoneRenderLoopDiagnostics({
    activeTab: tabState,
    phoneNumberLength: phoneNumber.length,
    searchValueLength: debounceSearchValue.value.length,
    hasActiveCall: !!activeCall,
    hasPendingInvitation: !!pendingInvitation,
    callListLength: callList.size,
    conferencedCallsLength: conferencedCalls?.length ?? 0,
    isAtMaxCallLimit,
    phoneStatus,
    hasRegistrationError: !!registrationError,
    hasAutoDialNumber: !!autoDialNumber,
    isAnyModalOpen,
    isOnline,
    contactSourcesLoadingState: contactSourcesLoadingState?.state ?? 'none',
    contactsListLoadingState: contactsListLoadingState?.state ?? 'none',
    hasSelectedContactSource: selectedContactSourceId != null,
    hasContactSources: !!contactSources,
  })

  const getPhoneDetailView = () => {
    const isInitializing = phoneStatus === PhoneStatus.INITIAL

    const isErrorState =
      !isOnline ||
      phoneStatus === PhoneStatus.ERROR ||
      phoneStatus === PhoneStatus.TRANSPORT_DISCONNECTED ||
      phoneStatus === PhoneStatus.MIC_DENIED

    const isOutOfCall = phoneStatus === PhoneStatus.READY

    const isInCall =
      activeCall &&
      (phoneStatus === PhoneStatus.CALL_CONNECTING ||
        phoneStatus === PhoneStatus.RINGING ||
        phoneStatus === PhoneStatus.CALL_ACTIVE)

    let detailView: ReactNode
    if (isInitializing) {
      detailView = <PhoneInitializingView onRetryClick={handleRetry} />
    }
    if (isOutOfCall) {
      detailView = (
        <OutOfCallView
          activeTab={tabState}
          onChangeTab={setTabState}
          phoneNumber={phoneNumber}
          searchValue={debounceSearchValue}
          searchedValue={contactsSearchValue}
          isSearching={hasValue(contactsSearchValue?.value)}
          isInputDisabled={isAnyModalOpen}
          onSearchTermChange={handleSearchTermChanged}
          searchResults={searchResults}
          onResultsEndReached={handleScrollEndReached}
          isSearchLoading={isSearchLoading}
          onStartCall={handleStartCall}
          onDialButtonClick={handleDialButtonClick}
          onDeleteButtonClick={handleDeleteButtonClick}
          onPhoneNumberChange={handlePhoneNumberChanged}
          isInputFocusRequested={isDialInputFocusRequested}
          onInputFocusRequestHandled={handleDialInputFocusRequestHandled}
        />
      )
    }
    if (isInCall) {
      const isInConference =
        conferencedCalls !== undefined && conferencedCalls.length > 1

      const isOnHold = isInConference ? conferenceIsOnHold : activeCall.isOnHold
      const isMuted = isInConference ? conferenceIsMuted : activeCall.isMuted

      const startTime = isInConference
        ? conferenceStartTime
        : activeCall.startTime

      const displayInfo = isInConference
        ? null
        : getDisplayCallerInfo(activeCall, true) // true = prefer SIP
      const phonenumber = displayInfo?.number

      detailView = (
        <InCallView
          callerNames={callerDisplayNames}
          phonenumber={phonenumber}
          dtmfHistory={dtmfHistory}
          callStartTime={startTime}
          isOnline={isOnline}
          isOnHold={isOnHold}
          callStatus={phoneStatus}
          isMuted={isMuted}
          onMuteClick={handleMuteClick}
          onHoldClick={handleHoldClick}
          onParkCall={handleParkCall}
          onTransferCall={handleTransferCall}
          pbxExtensions={extensions}
          parkingLots={trackedParkingLots}
          onDialButtonClick={handleDtmfClick}
          audioInputs={inputs}
          selectedInput={selectedInputDeviceId}
          onInputDeviceChange={handleInputDeviceChange}
          audioOutputs={outputs}
          selectedOutput={selectedOutputDeviceId}
          onOutputDeviceChange={handleOutputDeviceChange}
          onCallStop={handleEndCall}
          isBelowMaxCallLimit={!isAtMaxCallLimit}
          isInConference={isInConference}
          onNewCall={handleNewCallClick}
          onMergeCalls={handleMergeCalls}
          onSplitConference={handleSplitCalls}
        />
      )
    }
    if (isErrorState) {
      detailView = (
        <CallErrorView
          error={registrationError}
          isMicPermissionDenied={phoneStatus === PhoneStatus.MIC_DENIED}
          isConnected={isConnected}
          onRetryClick={handleRetry}
        />
      )
    }

    return (
      <Flex direction="column" boxSize="full" p="0.25rem">
        <Flex
          direction="column"
          boxSize="full"
          bg="bg.subtle"
          overflowY={'auto'}
          paddingBottom={isInCall ? '0' : '1rem'}
          paddingRight={3}
        >
          <ExtensionSelector
            hasActiveCall={!!activeCall}
            layoutProps={{
              h: { base: '3.75rem', lg: '5rem' },
            }}
          />
          {detailView}
        </Flex>
      </Flex>
    )
  }

  return (
    <ResponsivePage
      detailPanel={getPhoneDetailView}
      listPanel={
        <QuickDialView
          hasActiveCall={activeCall !== undefined}
          isAtMaxCallLimit={isAtMaxCallLimit}
          recentlyDialed={recentContacts}
          parkingLots={trackedParkingLots}
          extensions={trackedExtensions}
          speedDial={speedDial}
          onPark={handleParkCall}
          onTransfer={handleTransferCall}
          onStartCall={handleStartCall}
          onNewCall={handleNewCallWithTargetClick}
          onParkSettingsLinkClick={handlePhoneSettingsLinkClick}
        />
      }
    />
  )
}

export default WebPhoneView
